import { LocalStorageKey } from "../LocalStorageKey.js";

/**
 * Helper to define the theme states
 */
export enum Theme {
    Light = "light",
    Dark = "dark",
    Auto = "auto",
}

/**
 * Class to implement the themes functionality
 * as a singleton, which can be instantiated by
 * any other extension as a dependency
 */
export class Themes {
    public static readonly definer = "data-theme";
    public static readonly default = Theme.Light;

    /**
     * The private constructor
     * prevents direct instantiation
     */
    private constructor() {
        // Get the currently applying theme in order to
        // set it automatically should none be defined
        const _ = this.value;
    }

    /**
     * Method to instantiate the
     * themes on the current window
     *
     * @returns {Themes}
     */
    public static use(): Themes {
        if (!window.themes) {
            window.themes = new Themes();
        }
        return window.themes;
    }

    /**
     * Method to parse a string identifier of a theme
     *
     * @param {string | null} value
     * @returns {Theme | undefined}
     */
    public static parse(value: string | null): Theme | undefined {
        if (Object.values(Theme).includes(value as Theme)) {
            return value as Theme;
        }
        return undefined;
    }

    /**
     * Method to set the theme
     *
     * @param {Theme} theme
     * @returns {void}
     */
    public set value(theme: Theme) {
        document.documentElement.setAttribute(Themes.definer, theme);
        localStorage.setItem(LocalStorageKey.Theme, theme);
    }

    /**
     * Method to get the currently selected theme
     *
     * @returns {Theme}
     */
    public get value(): Theme {
        // Try to read the currently applying theme
        {
            const theme = Themes.parse(
                document.documentElement.getAttribute(Themes.definer),
            );
            if (theme) {
                this.value = theme;
                return theme;
            }
        }

        // Try to read the stored theme as a fallback,
        // otherwise set the default theme and return it
        const theme =
            Themes.parse(localStorage.getItem(LocalStorageKey.Theme)) ??
            Themes.default;
        this.value = theme;
        return theme;
    }

    /**
     * Method to apply the light theme
     *
     * @returns {void}
     */
    public light(): void {
        this.value = Theme.Light;
    }

    /**
     * Method to apply the dark theme
     *
     * @returns {void}
     */
    public dark(): void {
        this.value = Theme.Dark;
    }

    /**
     * Method to apply the auto theme
     *
     * @returns {void}
     */
    public auto(): void {
        this.value = Theme.Auto;
    }
}
